// frontend/src/components/user/Allcomplaints.js
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import api from '../../services/api';
import PriorityChip, { PRIORITY_LEVELS, normalizePriority } from '../common/PriorityChip';
import BulkActionsToolbar from '../common/BulkActionsToolbar';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend
);

const AllComplaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [selectedComplaints, setSelectedComplaints] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState('');
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('role') === 'admin';

  useEffect(() => {
    fetchComplaints();
  }, []);

  const fetchComplaints = async () => {
    try {
      // API interceptor already adds the token, no need to add it manually
      // This endpoint returns all complaints for admin, or user's own complaints for normal users
      const response = await api.get('/complaints');
      setComplaints(response.data || []);
    } catch (error) {
      console.error('Error fetching complaints:', error);
      // Error handling is done by API interceptor
      if (error.response?.status === 401) {
        // Session expired - handled by interceptor
        return;
      } else if (error.response?.status === 403) {
        alert('You do not have permission to view complaints. Please contact an administrator.');
      } else {
        alert('Failed to load complaints. Please try again.');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    navigate('/login');
  };

  const handleDelete = async (complaintId) => {
    if (window.confirm('Are you sure you want to delete this complaint?')) {
      try {
        // API interceptor already adds the token, no need to add it manually
        await api.delete(`/complaints/${complaintId}`);
        // Refresh the complaints list
        fetchComplaints();
      } catch (error) {
        console.error('Error deleting complaint:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to delete complaint';
        alert(errorMessage);
      }
    }
  };

  const handleSelectComplaint = (id) => {
    setSelectedComplaints(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedComplaints(filteredComplaints.map(c => c._id));
    } else {
      setSelectedComplaints([]);
    }
  };

  // Client-side priority filter (missing priority is treated as Medium)
  const filteredComplaints = complaints.filter(
    (complaint) => !priorityFilter || normalizePriority(complaint.priority) === priorityFilter
  );

  // Prepare chart data
  const statusCounts = complaints.reduce((acc, complaint) => {
    acc[complaint.status] = (acc[complaint.status] || 0) + 1;
    return acc;
  }, {});

  const chartData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        label: 'Complaints by Status',
        data: Object.values(statusCounts),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        tension: 0.1,
      },
    ],
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        {/* Top Action Bar with Back, New Complaint & Logout */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Go Back">
              <IconButton color="inherit" onClick={() => navigate(-1)}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h5" component="div" sx={{ ml: 1 }}>
              User Dashboard
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Submit a Complaint">
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                component={Link}
                to="/complaint_new"
                sx={{ mr: 2 }}
              >
                New Complaint
              </Button>
            </Tooltip>
            <Tooltip title="Logout">
              <IconButton color="secondary" onClick={handleLogout}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Analytics Chart */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Analytics: Complaints by Status
          </Typography>
          {chartData.labels.length > 0 ? (
            <Line data={chartData} />
          ) : (
            <Typography>No analytics data available.</Typography>
          )}
        </Box>

        {/* Complaints List */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 1 }}>
            <Typography variant="h6" gutterBottom>
              Your Complaints
            </Typography>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="priority-filter-label">Priority</InputLabel>
              <Select
                labelId="priority-filter-label"
                label="Priority"
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {PRIORITY_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {selectedComplaints.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <BulkActionsToolbar
                selectedIds={selectedComplaints}
                complaints={complaints.filter(c => selectedComplaints.includes(c._id))}
                isAdmin={isAdmin}
                onDone={() => {
                  setSelectedComplaints([]);
                  fetchComplaints();
                }}
              />
            </Box>
          )}
          {filteredComplaints.length > 0 ? (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      filteredComplaints.length > 0 &&
                      filteredComplaints.every(c => selectedComplaints.includes(c._id))
                    }
                    onChange={handleSelectAll}
                    inputProps={{ 'aria-label': 'Select All Complaints' }}
                  />
                }
                label="Select All"
              />
              <List>
                {filteredComplaints.map((complaint) => (
                  <ListItem
                    key={complaint._id}
                    sx={{
                      borderBottom: '1px solid #e0e0e0',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <Checkbox
                      checked={selectedComplaints.includes(complaint._id)}
                      onChange={() => handleSelectComplaint(complaint._id)}
                      inputProps={{ 'aria-label': `Select complaint ${complaint.subject}` }}
                    />
                    <ListItemText
                      primary={complaint.subject}
                      secondary={`Status: ${complaint.status} | Assigned To: ${complaint.assignedTo?.username || '—'}`}
                    />
                    <PriorityChip priority={complaint.priority} sx={{ mr: 1 }} />
                    <Tooltip title="Delete Complaint">
                      <IconButton color="error" onClick={() => handleDelete(complaint._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <Typography>No complaints found.</Typography>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default AllComplaints;
